import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { WorkflowConfigApiClient } from '~/services/Http';
import {
  IWorkflowConfigInputDto,
  IWorkflowConfigListResultDto,
  WorkflowConfigDto,
  WorkflowConfigInputDto,
  WorkflowConfigListResultDto,
} from '~/services/Http/client.iam.generated';

import { StoredUnitWorkflowConfigs, WorkflowConfig } from './types';

export type StoreState = {
  workflowConfigs: WorkflowConfig[];
  unitWorkflowConfigs: Record<string, StoredUnitWorkflowConfigs>;
  isLoading: boolean;
  failedToLoad: boolean;
};

const initialState: StoreState = {
  workflowConfigs: [],
  unitWorkflowConfigs: {},
  isLoading: false,
  failedToLoad: false,
};

export enum WorkflowConfigActionKeyPrefixes {
  ADD = 'data/workflow/ADD',
  GET = 'data/workflow/GET',
  GET_BY_UNIT = 'data/workflow/GET_BY_UNIT',
  UPDATE = 'data/workflow/UPDATE',
  REMOVE = 'data/workflow/REMOVE',
}

type WorkflowConfigsPayload = Omit<IWorkflowConfigListResultDto, 'data'> & {
  data: WorkflowConfig[];
};

const serializeWorkflowConfigDto = (
  workflowConfig: WorkflowConfigDto,
): WorkflowConfig => {
  // Convert class to plain object for redux state
  const { ...config } = workflowConfig;
  return config as unknown as WorkflowConfig;
};

export const getWorkflowConfigs = createAsyncThunk<WorkflowConfigListResultDto>(
  WorkflowConfigActionKeyPrefixes.GET,
  async (): Promise<WorkflowConfigListResultDto> => {
    return await WorkflowConfigApiClient.getWorkflows(undefined, '');
  },
);

export const getWorkflowConfigsByUnitId = createAsyncThunk<
  StoredUnitWorkflowConfigs,
  number
>(
  WorkflowConfigActionKeyPrefixes.GET_BY_UNIT,
  async (unitId: number): Promise<StoredUnitWorkflowConfigs> => {
    // TODO: Update when API changes unitId type from type String to Number
    const workflowConfigs = await WorkflowConfigApiClient.getWorkflows(
      unitId,
      '',
    );

    return {
      unitId,
      workflows: workflowConfigs.data.map((wConfig) => wConfig.id),
    };
  },
);

export const addWorkflowConfig = createAsyncThunk<
  WorkflowConfig,
  IWorkflowConfigInputDto
>(
  WorkflowConfigActionKeyPrefixes.ADD,
  async (workflowConfig: IWorkflowConfigInputDto): Promise<WorkflowConfig> => {
    if (!workflowConfig.id) {
      return Promise.reject();
    }

    const addedWorkflowConfig: WorkflowConfig =
      await WorkflowConfigApiClient.addWorkflowConfig(
        new WorkflowConfigInputDto(workflowConfig),
      ).then((data) => serializeWorkflowConfigDto(data));

    return addedWorkflowConfig;
  },
);

export const updateWorkflowConfig = createAsyncThunk<
  WorkflowConfig,
  IWorkflowConfigInputDto
>(
  WorkflowConfigActionKeyPrefixes.UPDATE,
  async (workflowConfig: IWorkflowConfigInputDto): Promise<WorkflowConfig> => {
    if (!workflowConfig.id) {
      return Promise.reject();
    }

    const updatedWorkflowConfig: WorkflowConfig =
      await WorkflowConfigApiClient.updateWorkflowConfig(
        workflowConfig.id,
        new WorkflowConfigInputDto(workflowConfig),
      ).then((data) => serializeWorkflowConfigDto(data));

    return updatedWorkflowConfig;
  },
);

export const removeWorkflowConfig = createAsyncThunk<string, string>(
  WorkflowConfigActionKeyPrefixes.REMOVE,
  async (workflowConfigId: string): Promise<string> => {
    await WorkflowConfigApiClient.removeWorkflowConfig(workflowConfigId);

    return workflowConfigId;
  },
);

export const workflowConfigSlice = createSlice({
  name: '~/data/workflows',
  initialState,
  reducers: {},
  extraReducers: {
    [getWorkflowConfigs.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<WorkflowConfigsPayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.workflowConfigs = action.payload.data;
    },
    [getWorkflowConfigs.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [getWorkflowConfigs.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [getWorkflowConfigsByUnitId.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<StoredUnitWorkflowConfigs>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.unitWorkflowConfigs[action.payload.unitId] = action.payload;
    },
    [getWorkflowConfigsByUnitId.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [getWorkflowConfigsByUnitId.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [addWorkflowConfig.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<WorkflowConfig>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.workflowConfigs.push(action.payload);
      state.unitWorkflowConfigs[action.payload.unitId].workflows.push(
        action.payload.id,
      );
    },
    [addWorkflowConfig.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [addWorkflowConfig.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [updateWorkflowConfig.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<WorkflowConfig>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.workflowConfigs = state.workflowConfigs.map((wConfig) => {
        if (!action.payload || wConfig.id !== action.payload.id) {
          return wConfig;
        }

        return action.payload;
      });
    },
    [updateWorkflowConfig.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [updateWorkflowConfig.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [removeWorkflowConfig.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<string>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.workflowConfigs = state.workflowConfigs.filter((wConfig) => {
        return wConfig.id !== action.payload;
      });
      state.unitWorkflowConfigs = Object.keys(state.unitWorkflowConfigs).reduce(
        (acc, unitId) => {
          if (
            !state.unitWorkflowConfigs[unitId] ||
            state.unitWorkflowConfigs[unitId].workflows
          ) {
            return acc;
          }

          return {
            acc,
            [unitId]: {
              ...state.unitWorkflowConfigs[unitId],
              workflows: state.unitWorkflowConfigs[unitId].workflows.filter(
                (workflowId) => workflowId !== action.payload,
              ),
            },
          };
        },
        {},
      );
    },
    [removeWorkflowConfig.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [removeWorkflowConfig.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const workflowConfigReducer = workflowConfigSlice.reducer;
